// The words a challenge's terms read as: one sentence per rule and step term,
// the card's headline line and structure label, a run's money words, and the
// one money text every page writes an amount with. They are
// authored once, in Rust (`packages/challenge-text`), and every host reads the
// same words: trading-app through its runtime module, the firm console through
// its own small wasm. A host installs the module it has; this file only carries
// the terms in and the words out.

import type { TemplateView } from '../wire';
import type { Amount } from './format';
import type { Step } from './headlineRules';

/** One sentence a card or the rules list shows. */
export interface RuleSentence {
  key: string;
  figure: string | null;
  text: string;
  fine: string | null;
  headline: boolean;
}

/** The handful of rules a card shows, with the currency their money is in. */
export interface HeadlineRules {
  accountSize: string;
  currency: string;
  profitTarget: Amount | null;
  maxDailyLoss: Amount | null;
  maxTotalLoss: { amount: Amount; trailing: boolean } | null;
  timeLimitDays: number | null;
  minTradingDays: number | null;
}

/** What an org sells (`organizations.governance`): simulations a trader
 *  starts with credits (Noctant's own org), or a firm's challenges, whose
 *  accounts the firm provides. */
export type OrgGovernance = 'credits' | 'plan';

/** An org's words for what it sells (challenge-text's `OrgWords`): every
 *  page, refusal and rule sentence of that org reads its nouns here. A run's
 *  money is never the org's: it reads `feeWords` and `payoutWords`. */
export interface OrgWords {
  governance: OrgGovernance;
  /** The area and its navigation: "Simulations" | "Challenges". */
  area: string;
  /** One run, inside a sentence: "simulation" | "challenge". */
  run: string;
  /** One run, as a label. */
  run_label: string;
  /** Runs, inside a sentence. */
  runs: string;
  /** The moment a run begins, as a label: "Start" | "Purchase". */
  acquired_label: string;
  /** What follows a run that ended. */
  next_run: string;
  /** Why the app starts no account on a firm; none on a credits org. */
  start_refused: string | null;
}

/** How a run's chain was started (its view's `origination_kind`): in the app,
 *  or provided by the firm that runs it. */
export type ChallengeOriginationKind = 'consumer_purchase' | 'operator_sponsored_attachment';

/** A run's fee (challenge-text's `FeeWords`), by how its chain was started:
 *  the fee shown is simulated on every run; the note says whose a real fee
 *  would be. */
export interface FeeWords {
  fee: string;
  fee_label: string;
  /** Said once beside the money a start, a restart or an activation moves. */
  fee_note: string | null;
}

/** A run's payouts (challenge-text's `PayoutWords`): simulated on every run.
 *  The settlement frozen into the run decides only the note, which a run its
 *  firm settles in real money carries. */
export interface PayoutWords {
  payout: string;
  payouts: string;
  payout_label: string;
  payouts_label: string;
  /** A funded account's status line. */
  funded: string;
  /** Who pays real money, said once: only on a run its firm settles. */
  payout_note: string | null;
}

/** challenge-text's JSON door, as a host's wasm exports it. */
export interface RuleTextModule {
  orgWords(governance: string): string;
  feeWords(started: string): string;
  replayRunFeeWords(): string;
  payoutWords(settlement: string, governance: string): string;
  stepSentences(accountSize: string, currency: string, step: string, governance: string): string;
  headlineRules(accountSize: string, currency: string, step: string): string;
  headlineLine(accountSize: string, currency: string, step: string): string;
  structureLabel(steps: string): string;
  formatDayBoundary(time: string, timezone: string): string;
  moneyText(amount: string, currency: string, style: string, sign: string): string;
  moneyMark(currency: string): string;
  cleanNumber(raw: string): string;
  sponsoredRefusal(templateCurrency: string, freshAccountStep: boolean): string;
}

let installed: RuleTextModule | null = null;

/** The host's module that speaks the words; installed once it has loaded. */
export function installRuleText(module: RuleTextModule): void {
  installed = module;
}

function words(): RuleTextModule {
  if (!installed) throw new Error('The rule text has not loaded yet.');
  return installed;
}

/** The terms the words read a step against: the account it trades and the
 *  currency the template is priced in. */
type Sized = Pick<TemplateView, 'account_size' | 'currency'>;
type StepTerms = Partial<Step>;

const spoken = new Map<OrgGovernance, OrgWords>();

/** The words of an org of `governance`. */
export function orgWords(governance: OrgGovernance): OrgWords {
  let read = spoken.get(governance);
  if (!read) {
    read = JSON.parse(words().orgWords(governance)) as OrgWords;
    spoken.set(governance, read);
  }
  return read;
}

/** challenge-text's answer, read: its empty answer (a token it does not
 *  read) is none. */
function answer<T>(json: string): T | null {
  return json === '' ? null : JSON.parse(json) as T;
}

const charged = new Map<ChallengeOriginationKind, FeeWords | null>();

/** The fee words of a run whose chain was `started` so; none for a start the
 *  installed words do not read. */
export function feeWords(started: ChallengeOriginationKind): FeeWords | null {
  let read = charged.get(started);
  if (read === undefined) {
    read = answer<FeeWords>(words().feeWords(started));
    charged.set(started, read);
  }
  return read;
}

export function replayRunFeeWords(): FeeWords {
  return JSON.parse(words().replayRunFeeWords()) as FeeWords;
}

const paid = new Map<string, PayoutWords | null>();

/** The payout words of a run that settles so, on an org of `governance` (the
 *  note names its run); none for a settlement the installed words do not
 *  read, which its host shows as no payout words at all. */
export function payoutWords(settlement: string, governance: OrgGovernance): PayoutWords | null {
  const key = `${settlement} ${governance}`;
  let read = paid.get(key);
  if (read === undefined) {
    read = answer<PayoutWords>(words().payoutWords(settlement, governance));
    paid.set(key, read);
  }
  return read;
}

/** Whether the words have loaded: a page shows none before. */
export function ruleTextLoaded(): boolean {
  return installed !== null;
}

/** Headline sentences first (target, daily loss, total loss, time, minimum
 *  days), then the remaining step terms, then the remaining rules, in the
 *  words of an org of `governance`, money in the template's currency (none
 *  for a currency the words do not read); the payouts a term counts are
 *  simulated on every run. */
export function stepSentences(t: Sized, step: StepTerms, governance: OrgGovernance): RuleSentence[] {
  return JSON.parse(words().stepSentences(String(t.account_size), t.currency, JSON.stringify(step), governance)) as RuleSentence[];
}

/** The card's handful of rules, with the template's currency; none for a
 *  currency the words do not read. */
export function headlineRules(t: Sized, step: StepTerms): HeadlineRules | null {
  const read = JSON.parse(words().headlineRules(String(t.account_size), t.currency, JSON.stringify(step))) as
    (Omit<HeadlineRules, 'accountSize'> & { accountSize: string | null }) | null;
  return read === null ? null : { ...read, accountSize: read.accountSize ?? '' };
}

/** "8% target · −5% day · −10% max (trailing) · 30 days · min 4 days" */
export function headlineLine(t: Sized, step: StepTerms): string {
  return words().headlineLine(String(t.account_size), t.currency, JSON.stringify(step));
}

/** "2 phases" | "1 phase" | "Instant funded" */
export function structureLabel(steps: StepTerms[]): { evalPhases: number; text: string } {
  return JSON.parse(words().structureLabel(JSON.stringify(steps))) as { evalPhases: number; text: string };
}

/** "5:00 PM New York time" */
export function formatDayBoundary(time: string, timezone: string): string {
  return words().formatDayBoundary(time, timezone);
}

/** How an amount looks: always cents ("$50,000.00"), cents only when present
 *  ("$2,000"), or compact ("$100K"). One money text, three named looks. */
export type MoneyStyle = 'cents' | 'cents_when_present' | 'compact';

/** A loss shows "-"; `always` also shows "+" (zero reads as a gain). */
export type MoneySign = 'negative' | 'always';

/** An amount (decimal text or a number) as money in `currency`, the ISO code
 *  its owner recorded ("$" for USD, the code for any other); "—" when there is
 *  no amount, or for a code the words do not read (never dollars). */
export function moneyText(
  amount: string | number | null | undefined,
  currency: string,
  style: MoneyStyle = 'cents',
  sign: MoneySign = 'negative',
): string {
  return words().moneyText(amount == null ? '' : String(amount), currency, style, sign);
}

/** The mark money in `currency` (an ISO code) is written with: "$" for USD,
 *  the code for any other; "—" for a code the words do not read. */
export function moneyMark(currency: string): string {
  return words().moneyMark(currency);
}

/** "10.0000" → "10"; the text as it is when it is not a number. */
export function cleanNumberText(raw: string): string {
  return words().cleanNumber(raw);
}

/**
 * Whether a template with these steps runs on an account the firm sponsors
 * (challenge-contracts' `UnsponsorableTemplate`, through challenge-text), as
 * the line the console shows. Its price is no reason: the attach records a
 * priced template's fee as the run's simulated purchase fee.
 */
export function sponsorshipLine(form: {
  readonly currency: string;
  readonly steps: readonly { freshAccount: boolean }[];
}): string {
  const answer = JSON.parse(words().sponsoredRefusal(
    form.currency,
    form.steps.some((step) => step.freshAccount),
  )) as { runs: true } | { runs: false; refusal: string };
  return answer.runs
    ? 'Runs on an account you sponsor.'
    : `${answer.refusal.charAt(0).toUpperCase()}${answer.refusal.slice(1)}.`;
}
